import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './index.css';

// Auth screens
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import AgeGate from './pages/auth/AgeGate';

// Enrollment
import ParentEnrollment from './pages/enrollment/ParentEnrollment';
import StudentAcceptInvite from './pages/enrollment/StudentAcceptInvite';
import EnrollmentSuccess from './pages/enrollment/EnrollmentSuccess';

// Parent app
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentSettings from './pages/parent/ParentSettings';
import ParentHistory from './pages/parent/ParentHistory';

// Student app
import StudentDashboard from './pages/student/StudentDashboard';
import StudentTransactions from './pages/student/StudentTransactions';
import StudentSavings from './pages/student/StudentSavings';
import StudentSettings from './pages/student/StudentSettings';
import StudentCloseOut from './pages/student/StudentCloseOut';
import SetGoalPage from './pages/student/SetGoalPage';
import StudentReviewSpend from './pages/student/StudentReviewSpend';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';

// Shared
import LoadingScreen from './components/shared/LoadingScreen';
import InstallPrompt from './components/shared/InstallPrompt';

// ─── Update banner ────────────────────────────────────────────────────────────
const UpdateBanner = ({ onUpdate }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
    background: '#2D6A4F', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: 'max(env(safe-area-inset-top), 10px) 16px 10px',
    fontSize: '0.875rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  }}>
    <span>A new version is available</span>
    <button
      onClick={onUpdate}
      style={{
        background: 'white', color: '#2D6A4F', border: 'none',
        borderRadius: 6, padding: '6px 14px', fontWeight: 700,
        fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'inherit',
      }}>
      Update now
    </button>
  </div>
);

// Redirects logged-out root visitors to the landing page
const LandingRedirect = () => {
  React.useEffect(() => {
    window.location.replace('/campus-wallet-landing.html');
  }, []);
  return null;
};

const AppRoutes = () => {
  const { authUser, userDoc, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  // Not logged in — root goes to landing page, everything else to signin
  if (!authUser) {
    return (
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup/student" element={<SignUp role="student" />} />
        <Route path="/invite/:token" element={<StudentAcceptInvite />} />
        <Route path="/invite/:familyId/:token" element={<StudentAcceptInvite />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  // Logged in but no user doc yet (race condition on first signup)
  if (!userDoc) return <LoadingScreen />;

  // Logged in, parent, not enrolled yet
  if (userDoc.role === 'parent' && !userDoc.familyId) {
    return (
      <Routes>
        <Route path="/enroll" element={<ParentEnrollment />} />
        <Route path="*" element={<Navigate to="/enroll" replace />} />
      </Routes>
    );
  }

  // Logged in, student, needs age confirmation — always go to age gate
  // AgeGate handles pending invite token acceptance after confirming age
  if (userDoc.role === 'student' && !userDoc.ageConfirmed) {
    return (
      <Routes>
        <Route path="/age-gate" element={<AgeGate />} />
        <Route path="*" element={<Navigate to="/age-gate" replace />} />
      </Routes>
    );
  }

  // Logged in, student, age confirmed but no family yet
  if (userDoc.role === 'student' && !userDoc.familyId) {
    // Invite just accepted — keep showing AgeGate (which shows success screen)
    // until familyId propagates. This prevents the blank screen race condition.
    if (sessionStorage.getItem('inviteJustAccepted')) {
      return (
        <Routes>
          <Route path="/age-gate" element={<AgeGate />} />
          <Route path="*" element={<Navigate to="/age-gate" replace />} />
        </Routes>
      );
    }
    // pendingInviteToken still present means AgeGate is mid-flow
    if (sessionStorage.getItem('pendingInviteToken')) {
      return (
        <Routes>
          <Route path="/age-gate" element={<AgeGate />} />
          <Route path="*" element={<Navigate to="/age-gate" replace />} />
        </Routes>
      );
    }
    const pendingToken = new URLSearchParams(window.location.search).get('invite');
    if (pendingToken) {
      return (
        <Routes>
          <Route path="*" element={<Navigate to={`/invite/${pendingToken}`} replace />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path="/invite/:token" element={<StudentAcceptInvite />} />
        <Route path="/invite/:familyId/:token" element={<StudentAcceptInvite />} />
        <Route path="/enrollment-success" element={<EnrollmentSuccess />} />
        <Route path="*" element={<Navigate to="/invite/pending" replace />} />
      </Routes>
    );
  }

  // Fully enrolled parent
  if (userDoc.role === 'parent') {
    return (
      <>
        <InstallPrompt studentName={null} />
        <Routes>
          <Route path="/" element={<ParentDashboard />} />
          <Route path="/history" element={<ParentHistory />} />
          <Route path="/settings" element={<ParentSettings />} />
          <Route path="/enrollment-success" element={<EnrollmentSuccess />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    );
  }

  // Fully enrolled student
  sessionStorage.removeItem('inviteJustAccepted');
  return (
    <>
      <InstallPrompt studentName={userDoc.displayName} />
      <Routes>
        <Route path="/" element={<StudentDashboard />} />
        <Route path="/transactions" element={<StudentTransactions />} />
        <Route path="/savings" element={<StudentSavings />} />
        <Route path="/settings" element={<StudentSettings />} />
        <Route path="/close-out" element={<StudentCloseOut />} />
        <Route path="/savings/goal/new" element={<SetGoalPage />} />
        <Route path="/review-spend" element={<StudentReviewSpend />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // Register service worker with update callback
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      import('./serviceWorkerRegistration').then(({ register }) => {
        register({
          onUpdate: (registration) => {
            setSwRegistration(registration);
            setShowUpdate(true);
          },
        });
      });
    }
  }, []);

  // Reload Firestore data when app comes back to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Dispatch a custom event that AuthContext can listen to
        window.dispatchEvent(new Event('app-foregrounded'));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleUpdate = () => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        {showUpdate && <UpdateBanner onUpdate={handleUpdate} />}
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
